import React, { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppContext, HomeItem } from '../AppContext';
import { useRef, useState } from "react";
import { useImmer } from "use-immer";
import { DndContext, DragOverlay, DragStartEvent, DragOverEvent, DragEndEvent } from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext
} from "@dnd-kit/sortable";
import Sidebar, { SidebarField } from './Sidebar';
import { Field } from './Canvas';
import { Form, Formik } from 'formik';
import NavCanvas from './NavCanvas';

interface FieldType {
  id: string;
  type: string;
  title: string;
  name?: string;
  size?: number;
  parent?: string | null;
  category?: string | null;
}

interface DataState {
  fields: FieldType[];
}

function getData(prop: any) {
  return prop?.data?.current ?? {};
}

function createSpacer({ id, isFull = false }: { id: string, isFull?: boolean }): FieldType {
  return {
    id,
    type: isFull ? "spacer full" : "spacer",
    title: "spacer",
    size: 10
  };
}

interface FormValues {
  homeItems: HomeItem[];
}

const Nav: React.FC = () => {
  const navigate = useNavigate();
  const context = useContext(AppContext); // Use o contexto

  // Verifica se o contexto está definido
  if (!context) {
    throw new Error('Step2 must be used within an AppProvider');
  }

  const { appData, updateAppData } = context;
  
  const [sidebarFieldsRegenKey, setSidebarFieldsRegenKey] = useState<number>(Date.now());
  const spacerInsertedRef = useRef<boolean>(false);
  const currentDragFieldRef = useRef<FieldType | null>(null);
  const [activeSidebarField, setActiveSidebarField] = useState<FieldType | null>(null);
  const [activeField, setActiveField] = useState<FieldType | null>(null);
  const [appBarPreview, setAppBarPreview] = useState(appData.appBarLogo || 'https://build.zas.media/images/appbar.png');
  const [homeData, updateHomeData] = useImmer<DataState>({
    fields: appData.homeItems ?? []
  });
  const [data, updateData] = useImmer<DataState>({
    fields: appData.navItems ?? []
  });

  const initialValues: FormValues = {
    homeItems: data.fields
  };

  const cleanUp = () => {
    setActiveSidebarField(null);
    setActiveField(null);
    currentDragFieldRef.current = null;
    spacerInsertedRef.current = false;
  };

  const handleDragStart = (e: DragStartEvent) => {
    const { active } = e;
    const activeData = getData(active);

    if (activeData.fromSidebar) {
      const { field } = activeData;
      const { type } = field;
      setActiveSidebarField(field);
      currentDragFieldRef.current = {
        id: String(active.id), // Convertido para string
        type,
        title: field.title || "Default Title", // Valor padrão
        name: `${type}${data.fields.length + 1}`,
        size: field.size,
        parent: null,
      };
      return;
    }

    const { field, index } = activeData;
    setActiveField(field);
    currentDragFieldRef.current = field;
    updateData((draft) => {
      draft.fields.splice(index, 1, createSpacer({ id: String(active.id), isFull: field.size === 3 }));
    });
  };

  const handleDragOver = (e: DragOverEvent) => {
    const { active, over } = e;
    const activeData = getData(active);

    if (activeData.fromSidebar) {
      const overData = getData(over);

      if (!spacerInsertedRef.current) {
        const spacer = createSpacer({ 
          id: `${active.id}-spacer`,
          isFull: activeData.field.size === 3
        });

        updateData((draft) => {
          const nextIndex = overData.index > -1 ? overData.index : draft.fields.length;
          draft.fields.splice(nextIndex, 0, spacer);

          spacerInsertedRef.current = true;
        });
      } else if (!over) {
        updateData((draft) => {
          draft.fields = draft.fields.filter((f) => f.type !== "spacer" && f.type !== "spacer full");
        });
        spacerInsertedRef.current = false;
      } else {
        updateData((draft) => {
          const spacerIndex = draft.fields.findIndex((f) => f.id === `${active.id}-spacer`);
          const nextIndex = overData.index > -1 ? overData.index : draft.fields.length - 1;

          if (nextIndex !== spacerIndex) {
            draft.fields = arrayMove(draft.fields, spacerIndex, overData.index);
          }
        });
      }
    }
  };

  const handleDragEnd = (e: DragEndEvent) => {
    const { over } = e;
    if (!over) {
      cleanUp();
      updateData((draft) => {
        draft.fields = draft.fields.filter((f) => f.type !== "spacer" && f.type !== "spacer full");
      });
      return;
    }

    const nextField = currentDragFieldRef.current;
    if (nextField) {
      const overData = getData(over);
      updateData((draft) => {
        const spacerIndex = draft.fields.findIndex((f) => f.type === "spacer" || f.type === "spacer full");
        draft.fields.splice(spacerIndex, 1, nextField);
        draft.fields = arrayMove(draft.fields, spacerIndex, overData.index || 0);
      });
    }

    setSidebarFieldsRegenKey(Date.now());
    cleanUp();
  };

  return (
    <div className="container">
      <div className="flex gap-4">
        <DndContext
          onDragStart={handleDragStart} 
          onDragOver={handleDragOver} 
          onDragEnd={handleDragEnd} autoScroll>
          <div className="w-2/8 content col">
            <div className="sidebar-wrap">
              <Sidebar 
                fieldsRegKey={String(sidebarFieldsRegenKey)}
                appType={appData.type}
                onlyOneThird={true}
                disabled={data.fields.filter(f => !f.type.includes("spacer")).length >= 3}
              />
            </div>
            <Link
              to={`/${appData.id}/inicial`}
              className="bg-primary text-white px-6 py-3 text-xl rounded-md flex items-center space-x-2"
            >
              <i className="fas fa-arrow-left"></i>
              <span>Tela inicial</span>
            </Link>
          </div>
          <div className="w-4/5 relative">
            <svg fill="none" className="dash"><defs><pattern x="0" y="0" id="pattern-dfa52911-fec3-4817-ac8e-19f444cb2809" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M-3 13 15-5M-5 5l18-18M-1 21 17 3"></path></pattern></defs><rect fill="url(#pattern-dfa52911-fec3-4817-ac8e-19f444cb2809)" width="100%" height="100%" stroke="none"></rect></svg>
            <div className='dash-over w-full screen-opacity'>
              <SortableContext strategy={horizontalListSortingStrategy} items={data.fields.map((f) => f.id)}>
                <NavCanvas fields={data.fields} homeFields={homeData.fields} appBarPreview={appBarPreview} />
              </SortableContext> 
              <DragOverlay dropAnimation={null}>
                {activeSidebarField && (
                  <SidebarField
                    overlay
                    field={{
                      ...activeSidebarField,
                      title: activeSidebarField.title || "Default Title", // Valor padrão
                      size: activeSidebarField?.size ?? 0,
                      category: activeSidebarField?.category || "general",
                    }}
                  />
                )}
                {activeField && <Field overlay field={activeField} />}
              </DragOverlay>
            </div>
          </div>
          <div className="w-2/5 content col">
            <div className='flex flex-col h-full justify-between'>
              <div>
                <p className="font-bold sm:text-2xl">Instruções</p>
                <div className="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3 mt-6" role="alert">
                  <p className="text-sm">Adicione até 3 itens extras à barra de navegação.</p>
                </div>
                <p className="font-bold mt-8">Adicionando um item</p>
                <ol className='list-decimal pl-5 mt-4'>
                  <li>Encontre o item desejado na lista</li>
                  <li>Clique no ícone laranja do item e, sem soltar, arraste-o para dentro da área da navegação</li>
                  <li>Solte o item na posição que deseja.</li>
                </ol>
                <p className="font-bold mt-4">Organizar Itens</p>
                <ol className='list-decimal pl-5 mt-4'>
                  <li>Dentro da área de navegação, clique no item que deseja mover.</li>
                  <li>Arraste-o para a nova posição e solte quando estiver no local desejado.</li>
                </ol>
                <p className="font-bold mt-4">Remover item</p>
                <ol className='list-decimal pl-5 mt-4'>
                  <li>Clique no item que deseja remover.</li>
                  <li>Arraste-o para fora da tela e solte para removê-lo.</li>
                </ol>
                <p className="font-bold mt-4">Alteração dos Títulos</p>
                <ol className='list-decimal pl-5 mt-4'>
                  <li>A alteração dos títulos pode ser solicitada ao nosso suporte</li>
                </ol>
              </div>
              <Formik
                initialValues={initialValues}
                onSubmit={(values) => {
                  updateAppData({ ...appData, navItems: data.fields });
                  navigate(`/${appData.id}/mais`);
                }}
              >
                 {({ setFieldValue }) => (
                  <Form>
                    <button
                      type="submit"
                      className="bg-primary text-white px-6 py-3 text-xl rounded-md flex items-center space-x-2 mt-4"
                    >
                      <span>Menu Mais</span>
                      <i className="fas fa-arrow-right"></i>
                    </button>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </DndContext>
      </div>
    </div>
  );
};

export default Nav;